import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, TrashSimple, Trash, Scroll } from "phosphor-react";
import TableWrapper from "components/common/table-wrapper";
import { DEBOUNCE_DELAY, initialMetaForTable } from "constants/common";
import CustomModal from "components/common/modal";
import AddOrganizationForm from "./AddOrganizationForm";
import { useNavigate } from "react-router-dom";
import { startEncounter } from "containers/community/api";
import { ReactComponent as FileIcon } from "./../../assets/images/file.svg";
import { highlightFirstMatch, capitalizeName } from "utils/common";
import Tooltip from "components/common/tooltip";
import {
  getOrganizations,
  createOrganizations,
  updateOrganizations,
  deleteOrganization,
  deleteBulkOrganizations,
} from "containers/community/api";
import { COMMUNITY_COLUMNS } from "constants/community";
let timeout;

const CommunityOutreach = () => {
  const navigate = useNavigate();

  const [organizationList, setOrganizationList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [meta, setMeta] = useState(initialMetaForTable);
  const [loading, setLoading] = useState(true);
  const [selectedOrganization, setSelectedOrganization] = useState(0);
  const [isOrganizationModalVisible, setOrganizationModalVisible] = useState(
    false
  );
  const [currentOrganizationEdit, setcurrentOrganizationEdit] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [refreshPage, setRefreshPage] = useState(false);

  const debounceFn = (callback, delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(callback, delay);
  };
  const handleSetSearchQuery = (value) => {
    setMeta((pre) => ({ ...pre, search: value }));
    debounceFn(handleRefreshPage, DEBOUNCE_DELAY);
  };
  const handlePageChange = (value) => {
    setMeta((pre) => ({ ...pre, page: value }));
    handleRefreshPage();
  };
  const handleRefreshPage = () => {
    setRefreshPage((pre) => !pre);
  };

  const handleSelectAll = () => {
    const updatedCheckboxes = organizationList.map((checkbox) => ({
      ...checkbox,
      checked: !selectAll,
    }));
    setOrganizationList(updatedCheckboxes);
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (index) => {
    setOrganizationList((prevList) =>
      prevList.map((item, itemIndex) =>
        itemIndex === index ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const handleCloseOrganizationModal = () => {
    setOrganizationModalVisible(false);
    setcurrentOrganizationEdit("");
  };

  const handleOpenOrganizationModal = () => {
    setOrganizationModalVisible(true);
  };
  const handleDeleteOrganization = async (id) => {
    const result = await deleteOrganization(id);
    if (result) handleRefreshPage();
  };
  const handleDeleteBulkOrganization = async () => {
    const selectedOutreachLeaderIds = organizationList
      ?.filter((organization) => organization.checked)
      .map((el) => el.id);
    const payload = {
      ids: selectedOutreachLeaderIds,
    };
    const result = await deleteBulkOrganizations(payload);
    if (result) handleRefreshPage();
  };
  const handleEncounterStart = async (organization) => {
    const {
      id,
      checked,
      name,
      owner,
      website,
      email,
      phone,
      address,
      city,
      state,
      zipCode,
      organization_contacts,
      count_of_encouters,
      total_enrolled_member,
      total_referrals_unique,
      primaryContactID,
    } = organization;
    try {
      const result = await startEncounter(id);
      if (result["encounter"]) {
        const encounterID = result.encounter.id;
        localStorage.setItem(
          "currentOrganization",
          JSON.stringify(organization)
        );
        navigate(`${name.replace(/[\s/]+/g, "-")}-encounter/${encounterID}`, {
          state: {
            keyContacts: organization_contacts || [],
            organizationInfo: organization || {},
          },
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleViewHistory = (organization) => {
    const { name, ...rest } = organization;
    localStorage.setItem("currentOrganization", JSON.stringify(organization));
    navigate(`${name.replace(/[\s/]+/g, "-")}-History`, {
      state: { organization },
    });
  };
  const handleOrganizationSubmission = async (
    organization,
    setSubmitting,
    setErrors
  ) => {
    const isEditMode = Boolean(currentOrganizationEdit);
    try {
      if (typeof currentOrganizationEdit === "object") {
        const { id, address, organization_contacts, ...rest } = organization;

        const payload = {
          ...rest,
        };

        const result = await updateOrganizations(
          {
            ...(isEditMode
              ? { community_engagement: payload }
              : { community_engagements: payload }),
          },
          id,
          setErrors
        );

        if (result) {
          handleCloseOrganizationModal();
          handleRefreshPage();
        }
      } else {
        const payload = {
          ...organization,
        };

        const result = await createOrganizations(
          {
            community_engagement: payload,
          },
          setErrors
        );
        if (result) {
          handleCloseOrganizationModal();
          handleRefreshPage();
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setSelectedOrganization(
      selectAll
        ? organizationList.length
        : organizationList.reduce((accumulator, item) => {
            if (item.checked) {
              return accumulator + 1;
            } else return accumulator;
          }, 0)
    );
  }, [organizationList, selectAll]);
  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getOrganizations({
        ...meta,
        key: sortConfig.key,
        order: sortConfig.direction,
      });
      if (result["community_engagements"]) {
        const data = result?.community_engagements.map((item) => ({
          ...item,
          checked: selectAll,
        }));
        setOrganizationList(data);
        setTotalCount(result?.meta.total_count);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [refreshPage, selectAll, meta, sortConfig.direction, sortConfig.key]);
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const SortableTableHeader = ({
    columnKey,
    label,
    sortConfig,
    onSortChange,
  }) => {
    const isSorting = sortConfig.key === columnKey;
    const isAscending = isSorting && sortConfig.direction === "asc";
    const isDescending = isSorting && sortConfig.direction === "desc";

    const handleSort = () => {
      if (isAscending) {
        onSortChange(columnKey, "desc");
      } else {
        onSortChange(columnKey, "asc");
      }
    };

    return (
      <th onClick={handleSort} style={{ cursor: "pointer" }}>
        <div className="d-flex position-relative">
          <div className="d-flex flex-column cursor-pointer me-2 position-absolute top-50  end-100 translate-middle-y ">
            {/* <CaretUp size={12} color={isAscending ? '#000000' : '#00000050'} weight="fill" />
            <CaretDown color={isDescending ? '#000000' : '#00000050'} size={12} weight="fill" /> */}
          </div>
          <div>{label}</div>
        </div>
      </th>
    );
  };

  return (
    <>
      <TableWrapper
        searchPlaceholder="Search Organization"
        setSearhQuery={handleSetSearchQuery}
        searchValue={meta.search}
        totalListCount={totalCount}
        pageSize={meta.perPage}
        currentPage={meta.page}
        isLoading={loading}
        onPageChange={handlePageChange}
        selectedItems={selectedOrganization}
        upload={true}
        uploadKey={"Organization"}
        actionButtons={
          selectedOrganization > 0
            ? [
                {
                  label: `Delete ${selectedOrganization} Items`,
                  classes: "danger-btn m-0-imp",
                  icon: <TrashSimple size={24} className="me-3" />,
                  handleClick: handleDeleteBulkOrganization,
                },
              ]
            : [
                {
                  label: "Add Organization",
                  classes: "primary-btn",
                  icon: <Plus size={24} className="me-3" />,
                  handleClick: handleOpenOrganizationModal,
                },
              ]
        }
      >
        <div className="container-fluid">
          <div className="table-responsive scrollable">
            <table className="table table-rows">
              <thead className="table-header">
                <tr>
                  <th>
                    <div className="form-check ps-3 mb-0">
                      <input
                        className="form-check-input light-grey-background"
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {COMMUNITY_COLUMNS.map((column) => (
                    <SortableTableHeader
                      key={column.columnKey}
                      columnKey={column.columnKey}
                      label={column.label}
                      isSortable={column.isSortable}
                      sortConfig={sortConfig}
                      onSortChange={handleSort}
                    />
                  ))}

                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {organizationList?.map((organization, index) => {
                  const {
                    id,
                    checked,
                    name,
                    owner,
                    website,
                    email,
                    phone,
                    address,
                    city,
                    state,
                    zipCode,
                    organization_contacts,
                    count_of_encouters,
                    total_enrolled_member,
                    total_referrals_unique,
                    primaryContactID,
                  } = organization;
                  const primaryContact =
                    organization_contacts?.find((contact) => contact.primary) ||
                    {};
                  return (
                    <tr key={index}>
                      <td>
                        <div className="form-check ps-3 mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleCheckboxChange(index)}
                          />
                        </div>
                      </td>

                      <td>{highlightFirstMatch(name, meta.search)}</td>

                      <td>{website}</td>

                      <td>
                        {highlightFirstMatch(primaryContact.name, meta.search)}
                        <br />
                        <span style={{ fontWeight: "300" }}>
                          {highlightFirstMatch(
                            primaryContact.designation,
                            meta.search
                          )}
                        </span>
                      </td>

                      <td>
                        {phone} <br />
                        {email}
                      </td>

                      <td>{count_of_encouters}</td>
                      <td>
                        {total_enrolled_member}/{total_referrals_unique}
                      </td>
                      <td>
                        <div
                          className={`status ${
                            organization.status === "active"
                              ? "active"
                              : "inactive"
                          }`}
                        >
                          {organization.status.charAt(0).toUpperCase() +
                            organization.status.slice(1)}
                        </div>
                      </td>

                      <td className="gap-2">
                        <Tooltip title="View">
                          <Scroll
                            size={24}
                            className="ms-3 opacity-50 cursor-pointer"
                            onClick={() => handleViewHistory(organization)}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Pencil
                            size={24}
                            className=" ms-3 opacity-50 cursor-pointer"
                            onClick={() => {
                              setcurrentOrganizationEdit(organization);
                              handleOpenOrganizationModal();
                            }}
                          />
                        </Tooltip>{" "}
                        <Tooltip title="Delete">
                          <Trash
                            size={24}
                            className="ms-3 opacity-50 cursor-pointer"
                            onClick={() => {
                              organization?.id &&
                                handleDeleteOrganization(organization.id);
                            }}
                          />
                        </Tooltip>
                      </td>

                      <td>
                        <button
                          className="secondary-btn text-black px-3 py-1 cursor-pointer"
                          type="button"
                          onClick={() => handleEncounterStart(organization)}
                        >
                          + Start Encounter
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </TableWrapper>
      {isOrganizationModalVisible && (
        <CustomModal
          size="sm"
          show
          onHide={handleCloseOrganizationModal}
          heading={`${currentOrganizationEdit ? "Edit" : "Add"} Organization`}
        >
          <AddOrganizationForm
            handleSubmittion={handleOrganizationSubmission}
            handleClose={handleCloseOrganizationModal}
            currentOrganizationEdit={currentOrganizationEdit}
          />
        </CustomModal>
      )}
    </>
  );
};

export default CommunityOutreach;
